import { call, delay, put, select, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import {
  fetchDraftDataRequest,
  fetchDraftDataSuccess,
  fetchDraftDataFailure,
  submitFormRequest,
  submitFormSuccess,
  submitFormFailure,
  resetFormState,
  fetchCompanyRequest,
  fetchCompanySuccess,
  fetchCompanyFailure,
} from '../addCompanySlice/addCompanySlice';
import { fetchCompanyListRequest } from '../companyListSlice/companyListSlice';
import { RootState } from '../store';
import serviceUtil from 'src/Utils/serviceUtil/serviceUtil';

const getFormConfig = (state: RootState) => state.addCompany.formConfig;
const getCompanyListState = (state: RootState) => state.companyList;

function* fetchDraftDataSaga(action: { payload: string | null }) {
  const companyId = action.payload;

  if (!companyId) return;

  try {
    const url = `${process.env.NEXT_PUBLIC_API_URL}/identity/company/${companyId}`;
    const response = yield call(serviceUtil.get, url, {});

    if (response.status === 200) {
      const draftData = response.data;
      const formConfig = yield select(getFormConfig);

      const updatedFieldState: { [key: string]: any } = {};
      const metaData: { [key: string]: any } = {};

      formConfig.forEach((step: any) => {
        step.fields.forEach((field: any) => {
          if (draftData[field.name] !== undefined) {
            updatedFieldState[field.name] = { value: draftData[field.name] };
          }
        });
      });

      Object.keys(draftData).forEach((key) => {
        const fieldExists = formConfig.some((step: any) => step.fields.some((field: any) => field.name === key));

        if (!fieldExists) {
          metaData[key] = draftData[key];
        }
      });

      yield put(fetchDraftDataSuccess({ fieldState: updatedFieldState, metaData, heading: 'Success', message: response.data.message }));
    } else {
      yield put(fetchDraftDataFailure({ heading: 'Error', message: response.error.response.data.message }));
    }
  } catch (error: any) {
    yield put(fetchDraftDataFailure(error.response?.data?.message));
  }
}

function* handleSubmitForm(action: any) {
  try {
    const { dataToSend, companyId, metaData } = action.payload;
    const url =
      metaData?.Form_Pending === true
        ? `${process.env.NEXT_PUBLIC_API_URL}/identity/company`
        : metaData?.companyAdminUpdate === true
          ? `${process.env.NEXT_PUBLIC_API_URL}/identity/company/update/${companyId}`
          : `${process.env.NEXT_PUBLIC_API_URL}/identity/company/${companyId}`;

    const response = metaData?.Form_Pending === true ? yield call(serviceUtil.post, url, dataToSend) : yield call(serviceUtil.patch, url, dataToSend);

    if (response.status === 201 || response.status === 200) {
      yield put(submitFormSuccess({ heading: 'Success', message: response.data.message }));
      yield put(resetFormState());
      localStorage.removeItem('company_id');
      localStorage.removeItem('vat');
      localStorage.removeItem('company_name');

      // Refetch company list with page 1 to show updated company first
      const companyListState: any = yield select(getCompanyListState);
      if (companyListState?.companyData) {
        yield put(
          fetchCompanyListRequest({
            search: companyListState.companyData.search || '',
            page: 1,
            perPage: companyListState.companyData.metaData?.pageSize || 10,
            sortBy: companyListState.companyData.metaData?.sortBy,
            sortOrder: companyListState.companyData.metaData?.sortOrder || 'DESC',
          })
        );
      }
    } else {
      yield put(submitFormFailure({ heading: 'Error', message: response.error.response.data.message }));
    }
  } catch (error) {
    yield put(submitFormFailure({ heading: 'Error', message: 'Error submitting form.' }));
  }
}

function* fetchCompanyData(action: { payload: { companyId: string; metaData: { [key: string]: any } } }) {
  try {
    const payload = action.payload;
    const companyId = payload.companyId;
    const isEdit = payload.metaData?.edit === true ? true : false;
    let url = '';
    if (isEdit) {
      url = `${process.env.NEXT_PUBLIC_API_URL}/identity/company/edit/${companyId}`;
    } else {
      url = `${process.env.NEXT_PUBLIC_API_URL}/identity/company/${companyId}`;
    }
    // yield put(resetFormState());
    yield delay(100);
    const response = yield call(serviceUtil.get, url, {});
    if (response.status === 200) {
      const companyData = response.data;
      const formConfig = yield select(getFormConfig);
      const updatedFieldState: { [key: string]: any } = {};
      const metaData: { [key: string]: any } = {};

      formConfig.forEach((step: any) => {
        step.fields.forEach((field: any) => {
          if (companyData[field.name] !== undefined) {
            if (field.logoData && companyData?.['logoFile']) {
              updatedFieldState[field.name] = { value: companyData[field.name], logoData: companyData['logoFile'] };
            } else {
              updatedFieldState[field.name] = { value: companyData[field.name] };
            }
          }
        });
      });

      Object.keys(companyData).forEach((key) => {
        const fieldExists = formConfig.some((step: any) => step.fields.some((field: any) => field.name === key));

        if (!fieldExists) {
          metaData[key] = companyData[key];
        }
      });

      yield put(fetchCompanySuccess({ fieldState: updatedFieldState, metaData, heading: 'Success', message: response.data.message }));
    } else {
      yield put(fetchCompanyFailure({ heading: 'Error', message: response.error.response.data.message }));
    }
  } catch (error) {
    yield put(fetchCompanyFailure({ heading: 'Error', message: 'Error submitting form.' }));
  }
}

export function* watchFetchCompany() {
  yield takeLatest(fetchCompanyRequest.type, fetchCompanyData);
  yield takeLatest(fetchDraftDataRequest.type, fetchDraftDataSaga);
  yield takeLatest(submitFormRequest.type, handleSubmitForm);
}
